import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, Link2 } from "lucide-react";
import { RuleIcon } from "./RuleIcon";

function RuleItem({ rule, prevGroup, category, suffix, copied, onCopy, index }) {
  const isCopied = copied === rule.anchor;
  return (
    <>
      {rule.group && rule.group !== prevGroup && (
        <li className="pt-6 first:pt-0" aria-hidden="true">
          <p className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em] text-eden-turquoise" data-testid={`rule-group-${rule.anchor}`}>
            <span className="h-px w-6 bg-eden-turquoise/60" />{rule.group}
          </p>
        </li>
      )}
      <li id={rule.anchor} className={`group relative grid gap-3 rounded-md border border-transparent p-4 transition-[background-color,border-color] duration-200 target:border-eden-orange/60 target:bg-eden-orange/5 hover:bg-eden-surface sm:grid-cols-[3.5rem_1fr_2rem] sm:p-5 animate-fade-up ${!rule.published ? "opacity-60" : ""}`} style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }} data-testid={`rule-${rule.anchor}`}>
        <a href={`/regras/${category.slug}${suffix}#${rule.anchor}`} className="inline-flex h-7 w-fit items-center rounded-sm bg-eden-elevated px-2 font-mono text-xs font-semibold text-eden-orange hover:bg-eden-orange hover:text-eden-bg" aria-label={`Ligação para a regra ${rule.number}`} data-testid={`rule-anchor-${rule.anchor}`}>{rule.number}</a>
        <div>
          {(rule.title || !rule.published) && (
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-eden-text sm:text-lg">
              {rule.title}
              {!rule.published && <span className="text-[10px] uppercase text-amber-400">rascunho</span>}
            </h3>
          )}
          {rule.body && <p className={`whitespace-pre-line text-sm leading-relaxed sm:text-[15px] ${rule.title ? "mt-2 text-eden-secondary" : "text-eden-text"}`}>{rule.body}</p>}
        </div>
        <button
          type="button"
          onClick={() => onCopy(rule, category)}
          className={`inline-flex h-8 w-8 items-center justify-center self-start rounded-sm border border-eden-border text-eden-muted transition-[opacity,color,border-color] hover:border-eden-orange hover:text-eden-orange focus:opacity-100 ${isCopied ? "border-emerald-500 text-emerald-400 opacity-100" : "sm:opacity-0 sm:group-hover:opacity-100"}`}
          aria-label={isCopied ? `Ligação da regra ${rule.number} copiada` : `Copiar ligação da regra ${rule.number}`}
          title="Copiar ligação"
          data-testid={`rule-copy-${rule.anchor}`}
        >
          {isCopied ? <Check size={13} aria-hidden="true" /> : <Link2 size={13} aria-hidden="true" />}
        </button>
      </li>
    </>
  );
}

export function RulesPanel({ category, prev, next, suffix, copied, onCopy, onSelect }) {
  const c = category;
  if (!c) return null;
  return (
    <section key={c.id} id={`cat-${c.slug}`} className="card-eden overflow-hidden" aria-labelledby={`cat-title-${c.slug}`} data-testid={`rules-panel-${c.slug}`}>
      <header className="relative border-b border-eden-border bg-eden-surface px-6 py-8 sm:px-8">
        <span className="pointer-events-none absolute right-6 top-6 text-eden-orange/10 sm:right-8" aria-hidden="true"><RuleIcon category={c} size={96} /></span>
        <p className="eyebrow">{c.rules.length} {c.rules.length === 1 ? "regra" : "regras"}</p>
        <h2 id={`cat-title-${c.slug}`} className="mt-3 text-2xl font-bold tracking-tight text-eden-text sm:text-3xl lg:text-4xl" data-testid={`rules-cat-toggle-${c.slug}`}>{c.title}</h2>
        {c.description && <p className="mt-3 max-w-2xl text-sm text-eden-secondary sm:text-base">{c.description}</p>}
      </header>
      <ol className="space-y-1 p-3 sm:p-4" data-testid={`cat-body-${c.slug}`}>
        {c.rules.length === 0 && <li className="p-6 text-sm text-eden-muted">Sem regras nesta categoria.</li>}
        {c.rules.map((r, ri) => (
          <RuleItem key={r.id} index={ri} rule={r} prevGroup={c.rules[ri - 1]?.group} category={c} suffix={suffix} copied={copied} onCopy={onCopy} />
        ))}
      </ol>
      <footer className="flex items-stretch justify-between gap-3 border-t border-eden-border p-3 sm:p-4">
        {prev ? (
          <Link to={`/regras/${prev.slug}${suffix}`} onClick={() => onSelect(prev.slug)} className="group flex min-w-0 flex-1 items-center gap-3 rounded-md border border-eden-border p-3 text-left transition-colors hover:border-eden-secondary" data-testid="rules-prev">
            <ChevronLeft size={18} className="shrink-0 text-eden-muted transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
            <span className="min-w-0"><span className="block font-mono text-[10px] uppercase tracking-widest text-eden-muted">Anterior</span><span className="block truncate text-sm font-medium text-eden-text">{prev.title}</span></span>
          </Link>
        ) : <span className="flex-1" />}
        {next ? (
          <Link to={`/regras/${next.slug}${suffix}`} onClick={() => onSelect(next.slug)} className="group flex min-w-0 flex-1 items-center justify-end gap-3 rounded-md border border-eden-border p-3 text-right transition-colors hover:border-eden-secondary" data-testid="rules-next">
            <span className="min-w-0"><span className="block font-mono text-[10px] uppercase tracking-widest text-eden-muted">Seguinte</span><span className="block truncate text-sm font-medium text-eden-text">{next.title}</span></span>
            <ChevronRight size={18} className="shrink-0 text-eden-muted transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </Link>
        ) : <span className="flex-1" />}
      </footer>
    </section>
  );
}
